/**
 * The CAD instance control: which instance is being driven, and a click to
 * switch.
 *
 * The trigger names the product, version, document and connected instance count.
 *
 * This is the app's selection **for one vendor** — the shell draws one of these
 * per connected vendor (#5), each listing only that vendor's instances. Chat,
 * skill execution and skill editing all address whatever is chosen here for
 * their vendor.
 *
 * **Two shapes, one control.** It was a line in a footer strip, and the team's
 * reading of that was that the connected Revit sat at the very bottom where
 * nobody looked. So it is now a `row` of the instance card at the top of the
 * sidebar — the document over "Revit 2024" — and, when the sidebar is hidden, a
 * `chip` in the title bar. The reason it was in the footer still holds and both
 * shapes keep it: the selection is a fact about the session rather than about
 * the screen you are on, so it has to be visible from every route. Someone
 * running a skill needs to know which Revit it is about to touch just as much as
 * someone typing a message.
 *
 * The list stays readable during a turn, but switching is locked: the tools
 * already address the instance chosen when the turn started.
 */
import { useEffect, useId, useRef, useState } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import { VENDOR_LABELS, instanceLabel, instanceParts, sortInstances, type HostKind } from "../lib/hostStatus";
import { canSwitchInstance, instanceAllowed, type InstanceRestriction, type Selection } from "../lib/selectedInstance";
import { anchorBelow, type AnchoredPanel } from "../lib/menuAnchor";
import type { CadInstance } from "../bimgo";

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function InstancesPopover({
  variant,
  vendor,
  instances,
  selection,
  busy,
  onSelect,
  dot,
  restriction = null,
}: {
  /** `row`: a line of the sidebar's instance card. `chip`: the title bar's, when the sidebar is hidden. */
  variant: "row" | "chip";
  /** Which vendor this control is for; `instances` are that vendor's only. */
  vendor: HostKind;
  instances: readonly CadInstance[];
  selection: Selection;
  /** A turn is streaming — the selection is pinned until it finishes. */
  busy: boolean;
  onSelect: (sessionId: string) => void;
  /** The status dot, rendered by the shell so the colour rules stay in one place. */
  dot: React.ReactNode;
  /** Years this vendor may switch to — an open AutoCAD skill's supported years. */
  restriction?: InstanceRestriction | null;
}) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const [panelStyle, setPanelStyle] = useState<AnchoredPanel | null>(null);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      if (!triggerRef.current || !menuRef.current) return;
      setPanelStyle(anchorBelow(triggerRef.current.getBoundingClientRect(), {
        width: window.innerWidth, height: window.innerHeight,
      }, menuRef.current.offsetWidth));
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (open && !menuRef.current?.contains(document.activeElement)) {
      const menu = menuRef.current;
      (menu?.querySelector<HTMLButtonElement>('[aria-checked="true"]')
        ?? menu?.querySelector<HTMLButtonElement>('[role="menuitemradio"]'))?.focus();
    }
  }, [open, instances, selection.instance?.sessionId]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const switchable = canSwitchInstance(busy, instances);

  // Execution only locks the options; the user may still inspect the list.
  useEffect(() => {
    if (instances.length === 0) setOpen(false);
  }, [instances.length]);

  const sorted = sortInstances(instances);
  // "Revit" rather than "Connected instances": with a control per vendor the
  // list is that vendor's, and the vendor's own name is the whole title.
  const panelTitle = VENDOR_LABELS[vendor];
  const untitled = t("about.untitled");
  // The vendor's own name stands in for the product line when nothing of it is
  // running — a pick whose Revit has closed still has a row, and it says so.
  const parts = selection.instance
    ? instanceParts(selection.instance, untitled)
    : { title: t("hosts.none"), product: panelTitle };
  const fullLabel = selection.instance ? instanceLabel(selection.instance, untitled) : `${panelTitle} · ${parts.title}`;
  const connectedLabel = t("instance.connectedCount", { count: instances.length });
  const hint = busy ? t("instance.lockedDuringTurn") : null;

  return (
    <div
      className={`status-popover is-${variant}`}
      ref={ref}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={variant === "row" ? "instance-row" : "titlebar-chip"}
        // Only the card's row answers for the hosts status: the title bar precedes the
        // shell in the DOM, and a `querySelector` for it must find the card.
        data-status={variant === "row" ? "hosts" : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`${fullLabel} · ${connectedLabel}${selection.instance ? ` · ${t("instance.current")}` : ""}`}
        disabled={instances.length === 0}
        title={busy ? `${fullLabel}\n${hint}` : fullLabel}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="instance-dot" aria-hidden="true">{dot}</span>
        {variant === "row" ? (
          <span className="instance-row-text">
            <span className="instance-row-doc">{parts.title}</span>
            <span className="instance-row-meta">{parts.product}</span>
          </span>
        ) : (
          <>
            <span className="titlebar-chip-product">{parts.product}</span>
            <span aria-hidden="true">·</span>
            <span className="titlebar-chip-label">{parts.title}</span>
          </>
        )}
        <span className="instance-count">{t("instance.totalCount", { count: instances.length })}</span>
        <span className="status-caret" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>

      {open ? (
        <div
          id={menuId}
          ref={menuRef}
          className="status-popover-panel"
          style={panelStyle ?? undefined}
          role="menu"
          aria-label={`${panelTitle} · ${connectedLabel}`}
          aria-describedby={hint ? `${menuId}-hint` : undefined}
          onKeyDown={(event) => {
            if (event.key === "Tab") {
              // Restore the trigger before the browser moves to the next control.
              close();
              return;
            }
            if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
            event.preventDefault();
            const options = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]'));
            const index = options.indexOf(document.activeElement as HTMLButtonElement);
            const next = event.key === "Home" ? 0 : event.key === "End" ? options.length - 1
              : (index + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length;
            options[next]?.focus();
          }}
        >
          <ul className="status-popover-list" role="none">
            {sorted.map((instance) => {
              const current = instance.sessionId === selection.instance?.sessionId;
              const option = instanceParts(instance, untitled);
              // An instance of a year the open skill does not support is listed but cannot
              // be picked, and says why (owner, 2026-10-24).
              const allowed = current || instanceAllowed(restriction, vendor, instance);
              return (
                // Keyed by session id, not pid: Windows recycles pids, and a
                // recycled key makes React reuse a row for a different Revit.
                <li key={instance.sessionId} role="none">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={current}
                    aria-disabled={!switchable}
                    tabIndex={current ? 0 : -1}
                    className="status-option"
                    disabled={!allowed}
                    title={
                      allowed
                        ? instanceLabel(instance, untitled)
                        : t("instance.unsupportedBySkill").replace(
                            "{version}",
                            instance.hostVersion ?? instance.revitVersion ?? "",
                          )
                    }
                    onClick={() => {
                      if (!switchable) return;
                      onSelect(instance.sessionId);
                      close();
                    }}
                  >
                    <span className="status-option-label">
                      <span className="status-option-product">{option.product}</span>
                      <span className="status-option-document">{option.title}</span>
                    </span>
                    <span className="status-option-tick" aria-hidden="true">{current ? <CheckIcon /> : null}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {hint ? <div className="status-popover-hint" id={`${menuId}-hint`}>{hint}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
